import { useEffect, useState, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCommunitiesContext } from "../hooks/useCommunitiesContext";
import CommunityDetails from "../components/CommunityDetails";
import Fuse from "fuse.js";
import { FaSearch, FaTimes } from "react-icons/fa";
import "./GroupSearch.css";
import { getVisitorId } from "../utils/abTest";
import { isInternalVisit } from "../hooks/useCommunityPageAnalytics";

// Report searches / opened groups for admin analytics (backend
// models/groupSearchLogModel.js) — best-effort, never blocks the page.
const PAGE_LOAD_ID = `gs_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
const logGroupSearch = (data) => {
  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (!apiBase) return;
  try {
    fetch(`${apiBase}/analytics/group-search`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({ visitorId: getVisitorId(), pageLoadId: PAGE_LOAD_ID, internal: isInternalVisit(), ...data }),
    }).catch(() => {});
  } catch { /* ignore */ }
};

const GroupSearch = () => {
  const { communities, dispatch } = useCommunitiesContext();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);

  useEffect(() => {
    const fetchCommunities = async () => {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/communities`);
      const json = await response.json();
      if (response.ok) {
        dispatch({ type: "SET_COMMUNITIES", payload: json });
      }
    };
    fetchCommunities();
  }, [dispatch]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tagParam = urlParams.get("tag");
    if (tagParam && communities.length > 0) {
      setSelectedFilters([decodeURIComponent(tagParam)]);
      const newUrl = new URL(window.location);
      newUrl.searchParams.delete("tag");
      window.history.replaceState({}, "", newUrl.pathname);
    }
  }, [location.search, communities]);

  const fuse = useMemo(() => new Fuse(communities, {
    keys: [
      { name: "title", weight: 4 },
      { name: "location", weight: 3 },
      { name: "tags", weight: 2 },
      { name: "topLevelSummary", weight: 1.5 },
      { name: "homePageTagline", weight: 1.5 },
      { name: "groupinfo", weight: 1 },
      { name: "mainDescription", weight: 0.5 },
    ],
    includeScore: true,
    threshold: 0.4,
    ignoreLocation: true,
    minMatchCharLength: 2,
  }), [communities]);

  const locations = useMemo(() => {
    const locs = new Set();
    communities.forEach(c => { if (c.location) locs.add(c.location); });
    return Array.from(locs).sort();
  }, [communities]);

  const getTagsArray = (c) => {
    if (!c.tags) return [];
    return typeof c.tags === "string"
      ? c.tags.split(",").map(t => t.trim()).filter(Boolean)
      : c.tags.map(t => t.trim()).filter(Boolean);
  };

  const results = useMemo(() => {
    let base = query.trim()
      ? fuse.search(query.trim()).map(r => r.item)
      : [...communities];

    if (selectedFilters.length > 0) {
      base = base.filter(c => {
        const tags = getTagsArray(c);
        const loc = c.location || "";
        return selectedFilters.every(f => tags.includes(f) || loc === f);
      });
    }
    return base;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedFilters, fuse, communities]);

  const isFiltering = query.trim().length > 0 || selectedFilters.length > 0;

  // Log a search once they've stopped typing / picking for a moment, and
  // not the same search twice in a row.
  const lastLogged = useRef('');
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 && selectedFilters.length === 0) return undefined;
    const key = JSON.stringify([q.toLowerCase(), [...selectedFilters].sort()]);
    if (key === lastLogged.current) return undefined;
    const t = setTimeout(() => {
      lastLogged.current = key;
      logGroupSearch({
        kind: 'search',
        query: q,
        filters: selectedFilters,
        resultCount: results.length,
        topResults: results.slice(0, 5).map(c => c.title),
      });
    }, 1500);
    return () => clearTimeout(t);
  }, [query, selectedFilters, results]);

  // Only a link / button opens a group (tags just filter the list).
  const logOpened = (c, e) => {
    const el = e.target.closest && e.target.closest('a, button');
    if (!el) return;
    logGroupSearch({
      kind: 'open',
      via: el.tagName === 'BUTTON' ? 'join' : 'view',
      query: query.trim(),
      filters: selectedFilters,
      openedTitle: c.title,
      openedId: c._id,
    });
  };

  const toggleFilter = (f) => {
    setSelectedFilters(prev =>
      prev.includes(f) ? prev.filter(t => t !== f) : [...prev, f]
    );
  };

  const clearAll = () => {
    setQuery("");
    setSelectedFilters([]);
  };

  return (
    <div className="home">
      <div className="introhome">
        <h1><strong>Find a group</strong></h1>
        <p>...then match with others in it</p>
      </div>

      <div className="gs-search-bar-wrap">
        <div className="gs-search-input-wrap">
          <FaSearch className="gs-search-icon" />
          <input
            className="gs-search-input"
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, location, interest..."
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          {query && (
            <button className="gs-search-clear" onClick={() => setQuery("")} aria-label="Clear search">
              <FaTimes />
            </button>
          )}
        </div>

      </div>

      <p className="gs-create-prompt">
        Can't find an Interconnector group you're after? <a href="/newcommunityentry">Create your own!</a> Takes 3 min!!
      </p>

      {isFiltering && (
        <div className="gs-results-header">
          <span className="gs-results-count">
            {results.length} group{results.length !== 1 ? "s" : ""} found
          </span>
          <button className="gs-clear-all" onClick={clearAll}>
            Clear all
          </button>
        </div>
      )}

      <div className="results-section">
        {results.length > 0 ? (
          <>
            {!isFiltering && <p className="gs-section-label">Featured Communities</p>}
            <div className="results-grid">
              {results.map(c => (
                // display: contents keeps the grid layout; the capture
                // listener notes which group was opened.
                <div key={c._id} style={{ display: 'contents' }} onClickCapture={(e) => logOpened(c, e)}>
                  <CommunityDetails
                    community={c}
                    onTagClick={toggleFilter}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="gs-no-results">
            <p>No communities found{query ? ` for "${query}"` : ""}.</p>
            <p><a href="/newcommunityentry">Create one!</a></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupSearch;
